import { z } from "zod";
import { InquiryStageSchema } from "./inquiry";
import { PackageCodeSchema } from "./package";

/**
 * Phase 8's owner-home-screen contract — GET /v2/dashboard/summary plus the
 * two CSV-backed reports (GET /v2/reports/booking-sources,
 * GET /v2/reports/monthly-contribution). Every number here is
 * server-computed (pipeline value, contribution, package mix %, SLA %,
 * review count) — this file never re-derives a KPI from raw rows, per the
 * house rule that counts/money are server-authoritative and rendered via
 * centsToUsd, never computed client-side.
 */

// ---- Pipeline ---------------------------------------------------------------

export const PipelineStageRowSchema = z.object({
  stage: InquiryStageSchema,
  count: z.number(),
  value_cents: z.number(),
});
export type PipelineStageRow = z.infer<typeof PipelineStageRowSchema>;

// ---- Contribution -------------------------------------------------------------

export const ContributionMonthRowSchema = z.object({
  month: z.string(), // "YYYY-MM"
  contribution_cents: z.number(),
});
export type ContributionMonthRow = z.infer<typeof ContributionMonthRowSchema>;

// ---- Package mix -------------------------------------------------------------

export const PackageMixRowSchema = z.object({
  code: PackageCodeSchema,
  booked_count: z.number(),
  pct: z.number(),
});
export type PackageMixRow = z.infer<typeof PackageMixRowSchema>;

/**
 * The playbook's 60/20/20 Signature/Classic/Story mix target. `zero_proof`
 * has no target in the contract (it isn't part of the ramp's core mix), so
 * this schema only covers the three keys the plan calls out.
 */
export const TargetPctSchema = z.object({
  signature: z.number(),
  classic: z.number(),
  story: z.number(),
});
export type TargetPct = z.infer<typeof TargetPctSchema>;

// ---- Reviews / SLA -------------------------------------------------------------

export const DashboardReviewCountSchema = z.object({
  total: z.number(),
  target: z.number(),
  remaining: z.number(),
});
export type DashboardReviewCount = z.infer<typeof DashboardReviewCountSchema>;

export const DashboardSlaSchema = z.object({
  answered_within_2h: z.number(),
  total_inquiries: z.number(),
  pct: z.number(),
});
export type DashboardSla = z.infer<typeof DashboardSlaSchema>;

// ---- Upcoming tasks -------------------------------------------------------------

/**
 * `kind` is a free-form discriminator (event T-minus task vs. outreach
 * touch vs. proof-engine step, per the plan's several cadences that all
 * feed "what's due this week") — the contract doesn't pin it to an enum,
 * so it's rendered as-is rather than mapped through a fixed label table.
 */
export const UpcomingTaskSchema = z.object({
  title: z.string(),
  due_date: z.string().nullable(),
  event_id: z.string().nullable(),
  kind: z.string(),
});
export type UpcomingTask = z.infer<typeof UpcomingTaskSchema>;

// ---- Summary ------------------------------------------------------------------

export const DashboardSummarySchema = z.object({
  pipeline: z.object({
    by_stage: z.array(PipelineStageRowSchema),
  }),
  contribution_mtd_cents: z.number(),
  contribution_by_month: z.array(ContributionMonthRowSchema),
  package_mix: z.array(PackageMixRowSchema),
  package_mix_target_pct: TargetPctSchema,
  review_count: DashboardReviewCountSchema,
  sla: DashboardSlaSchema,
  upcoming_tasks: z.array(UpcomingTaskSchema),
  this_week_events: z.number(),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;

// ---- Reports --------------------------------------------------------------------

export const BookingSourceRowSchema = z.object({
  source: z.string(),
  inquiries: z.number(),
  booked: z.number(),
  close_rate_pct: z.number(),
});
export type BookingSourceRow = z.infer<typeof BookingSourceRowSchema>;

export const BookingSourcesResponseSchema = z.array(BookingSourceRowSchema);
export type BookingSourcesResponse = z.infer<typeof BookingSourcesResponseSchema>;

/**
 * Distinct from ContributionMonthRow above: this report row carries
 * `target_cents` (the ramp table's per-month target), which the dashboard
 * summary's `contribution_by_month` does not — the "contribution vs ramp"
 * widget reads from this report, not from the summary series, specifically
 * so it can color bars green/red against a real target.
 */
export const MonthlyContributionRowSchema = z.object({
  month: z.string(), // "YYYY-MM"
  contribution_cents: z.number(),
  target_cents: z.number(),
});
export type MonthlyContributionRow = z.infer<typeof MonthlyContributionRowSchema>;

export const MonthlyContributionResponseSchema = z.array(MonthlyContributionRowSchema);
export type MonthlyContributionResponse = z.infer<typeof MonthlyContributionResponseSchema>;
